import { api } from './api'
import { VitalSigns, VitalSignsFormData, VitalSignsFilters, VitalSignsSummary } from '../types/vitalSigns'
import { calculateBMI } from '../utils/vitalSignsUtils'

export const vitalSignsService = {
  /**
   * Get all vital signs with optional filters
   */
  getAll: async (filters?: VitalSignsFilters): Promise<VitalSigns[]> => {
    const response = await api.get<VitalSigns[]>('/vital-signs', {
      params: filters,
    })
    return response.data
  },

  /**
   * Get vital signs by ID
   */
  getById: async (id: string): Promise<VitalSigns> => {
    const response = await api.get<VitalSigns>(`/vital-signs/${id}`)
    return response.data
  },

  /**
   * Get vital signs for a specific patient
   */
  getByPatient: async (patientId: string): Promise<VitalSigns[]> => {
    const response = await api.get<VitalSigns[]>('/vital-signs', {
      params: { patientId },
    })
    return response.data
  },

  /**
   * Get vital signs for a specific consultation
   */
  getByConsultation: async (consultationId: string): Promise<VitalSigns[]> => {
    const response = await api.get<VitalSigns[]>('/vital-signs', {
      params: { consultationId },
    })
    return response.data
  },

  /**
   * Get vital signs summary for a patient
   */
  getPatientSummary: async (patientId: string): Promise<VitalSignsSummary> => {
    const response = await api.get<VitalSignsSummary>(`/vital-signs/patient/${patientId}/summary`)
    return response.data
  },

  /**
   * Create a new vital signs record
   * Converts form data to API format (handles unit conversion if needed, but backend expects SI units)
   */
  create: async (data: VitalSignsFormData): Promise<VitalSigns> => {
    // Convert form data to API format
    const apiData: any = {
      patientId: data.patientId,
      consultationId: data.consultationId,
      recordedAt: data.recordedAt || new Date().toISOString(),
      // recordedBy should be set by frontend from user context
      recordedBy: data.recordedBy,
      recordedByName: data.recordedByName,
    }

    // Cardiovascular
    if (data.bloodPressureSystolic !== undefined || data.bloodPressureDiastolic !== undefined) {
      apiData.bloodPressure = {
        systolic: data.bloodPressureSystolic,
        diastolic: data.bloodPressureDiastolic,
        unit: 'mmHg',
      }
    }
    if (data.heartRate !== undefined) {
      apiData.heartRate = { value: data.heartRate, unit: 'bpm' }
    }
    if (data.pulseRhythm) {
      apiData.pulseRhythm = data.pulseRhythm
    }
    if (data.ejectionFraction !== undefined) {
      apiData.ejectionFraction = { value: data.ejectionFraction, unit: '%' }
    }

    // Respiratory
    if (data.respiratoryRate !== undefined) {
      apiData.respiratoryRate = { value: data.respiratoryRate, unit: 'breaths/min' }
    }
    if (data.peakFlow !== undefined) {
      apiData.peakFlow = { value: data.peakFlow, unit: 'L/min' }
    }
    if (data.oxygenSaturation !== undefined) {
      apiData.oxygenSaturation = { value: data.oxygenSaturation, unit: '%' }
    }

    // Temperature - backend expects celsius, so convert if needed
    if (data.temperature !== undefined) {
      apiData.temperature = {
        value: data.temperature,
        unit: data.temperatureUnit || 'celsius',
      }
    }

    // Physical Measurements - backend expects SI units (cm, kg), convert if needed
    if (data.height !== undefined) {
      apiData.height = {
        value: data.height,
        unit: data.heightUnit || 'cm',
      }
    }
    if (data.weight !== undefined) {
      apiData.weight = {
        value: data.weight,
        unit: data.weightUnit || 'kg',
      }
    }
    if (data.abdominalCircumference !== undefined) {
      apiData.abdominalCircumference = {
        value: data.abdominalCircumference,
        unit: data.abdominalCircumferenceUnit || 'cm',
      }
    }
    if (data.headCircumference !== undefined) {
      apiData.headCircumference = {
        value: data.headCircumference,
        unit: data.headCircumferenceUnit || 'cm',
      }
    }

    // Calculate BMI if height and weight are provided
    if (apiData.height && apiData.weight && apiData.height.value && apiData.weight.value) {
      // Convert to cm and kg for BMI calculation
      let heightCm = apiData.height.value
      let weightKg = apiData.weight.value
      
      if (apiData.height.unit === 'in') {
        heightCm = apiData.height.value * 2.54
      }
      if (apiData.weight.unit === 'lb') {
        weightKg = apiData.weight.value * 0.453592
      } else if (apiData.weight.unit === 'stone') {
        weightKg = apiData.weight.value * 6.35029
      }
      
      if (heightCm > 0 && weightKg > 0) {
        const bmi = calculateBMI(heightCm, weightKg)
        if (bmi && bmi > 0) {
          apiData.bmi = bmi
        }
      }
    }

    // Lipid Panel
    if (data.cholesterol !== undefined) {
      apiData.cholesterol = { value: data.cholesterol, unit: 'mmol/L' }
    }
    if (data.ldl !== undefined) {
      apiData.ldl = { value: data.ldl, unit: 'mmol/L' }
    }
    if (data.hdl !== undefined) {
      apiData.hdl = { value: data.hdl, unit: 'mmol/L' }
    }

    // Pain Assessment
    if (data.painSeverity !== undefined) {
      apiData.painSeverity = {
        value: data.painSeverity,
        scale: data.painScale || 'nrs',
      }
    }

    // Lifestyle & Social
    if (data.physicalExercise) {
      apiData.physicalExercise = data.physicalExercise
    }
    if (data.substanceMisuse !== undefined) {
      apiData.substanceMisuse = data.substanceMisuse
    }

    // Percentiles
    if (data.heightPercentile !== undefined) {
      apiData.heightPercentile = data.heightPercentile
    }
    if (data.weightPercentile !== undefined) {
      apiData.weightPercentile = data.weightPercentile
    }
    if (data.bmiPercentile !== undefined) {
      apiData.bmiPercentile = data.bmiPercentile
    }
    if (data.headCircumferencePercentile !== undefined) {
      apiData.headCircumferencePercentile = data.headCircumferencePercentile
    }

    // Unstructured Data
    if (data.notes !== undefined) {
      apiData.notes = data.notes
    }
    if (data.source) {
      apiData.source = data.source
    }

    // Add BMI if calculated
    if (apiData.bmi !== undefined) {
      // Already set above
    }

    const response = await api.post<VitalSigns>('/vital-signs', apiData)
    return response.data
  },

  /**
   * Update a vital signs record
   */
  update: async (id: string, data: Partial<VitalSignsFormData>): Promise<VitalSigns> => {
    // Convert form data to API format (same as create)
    const apiData: any = {}

    if (data.bloodPressureSystolic !== undefined || data.bloodPressureDiastolic !== undefined) {
      apiData.bloodPressure = {
        systolic: data.bloodPressureSystolic,
        diastolic: data.bloodPressureDiastolic,
        unit: 'mmHg',
      }
    }
    if (data.heartRate !== undefined) {
      apiData.heartRate = { value: data.heartRate, unit: 'bpm' }
    }
    if (data.pulseRhythm !== undefined) {
      apiData.pulseRhythm = data.pulseRhythm
    }
    if (data.ejectionFraction !== undefined) {
      apiData.ejectionFraction = { value: data.ejectionFraction, unit: '%' }
    }
    if (data.respiratoryRate !== undefined) {
      apiData.respiratoryRate = { value: data.respiratoryRate, unit: 'breaths/min' }
    }
    if (data.peakFlow !== undefined) {
      apiData.peakFlow = { value: data.peakFlow, unit: 'L/min' }
    }
    if (data.oxygenSaturation !== undefined) {
      apiData.oxygenSaturation = { value: data.oxygenSaturation, unit: '%' }
    }
    if (data.temperature !== undefined) {
      apiData.temperature = {
        value: data.temperature,
        unit: data.temperatureUnit || 'celsius',
      }
    }
    if (data.height !== undefined) {
      apiData.height = {
        value: data.height,
        unit: data.heightUnit || 'cm',
      }
    }
    if (data.weight !== undefined) {
      apiData.weight = {
        value: data.weight,
        unit: data.weightUnit || 'kg',
      }
    }
    if (data.abdominalCircumference !== undefined) {
      apiData.abdominalCircumference = {
        value: data.abdominalCircumference,
        unit: data.abdominalCircumferenceUnit || 'cm',
      }
    }
    if (data.headCircumference !== undefined) {
      apiData.headCircumference = {
        value: data.headCircumference,
        unit: data.headCircumferenceUnit || 'cm',
      }
    }
    if (data.cholesterol !== undefined) {
      apiData.cholesterol = { value: data.cholesterol, unit: 'mmol/L' }
    }
    if (data.ldl !== undefined) {
      apiData.ldl = { value: data.ldl, unit: 'mmol/L' }
    }
    if (data.hdl !== undefined) {
      apiData.hdl = { value: data.hdl, unit: 'mmol/L' }
    }
    if (data.painSeverity !== undefined) {
      apiData.painSeverity = {
        value: data.painSeverity,
        scale: data.painScale || 'nrs',
      }
    }
    if (data.physicalExercise !== undefined) {
      apiData.physicalExercise = data.physicalExercise
    }
    if (data.substanceMisuse !== undefined) {
      apiData.substanceMisuse = data.substanceMisuse
    }
    if (data.heightPercentile !== undefined) {
      apiData.heightPercentile = data.heightPercentile
    }
    if (data.weightPercentile !== undefined) {
      apiData.weightPercentile = data.weightPercentile
    }
    if (data.bmiPercentile !== undefined) {
      apiData.bmiPercentile = data.bmiPercentile
    }
    if (data.headCircumferencePercentile !== undefined) {
      apiData.headCircumferencePercentile = data.headCircumferencePercentile
    }
    if (data.notes !== undefined) {
      apiData.notes = data.notes
    }
    if (data.source !== undefined) {
      apiData.source = data.source
    }

    const response = await api.put<VitalSigns>(`/vital-signs/${id}`, apiData)
    return response.data
  },

  /**
   * Delete a vital signs record
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/vital-signs/${id}`)
  },
}
